import { IElementBounds, IPositionParams, TPosition } from '../../grid.types'

const calcGridColWidth = (positionParams: IPositionParams): number => {
  if (positionParams?.cols <= 0) return 0
  const { margin, containerPadding, containerWidth, cols } = positionParams
  return (containerWidth - margin[0] * (cols - 1) - containerPadding[0] * 2) / cols
}

/**
 * This can either be called:
 * calcGridItemWHPx(w, colWidth, margin[0])
 * or
 * calcGridItemWHPx(h, rowHeight, margin[1])                 Object containing coords.
 */
const calcGridItemWHPx = (gridUnits: number, colOrRowSize: number, marginPx: number): number => {
  // 0 * Infinity === NaN, which causes problems with resize contraints
  if (!Number.isFinite(gridUnits)) return gridUnits
  return Math.round(colOrRowSize * gridUnits + Math.max(0, gridUnits - 1) * marginPx)
}
/**
 * Return position on the page given an x, y, w, h.
 * left, top, width, height are all in pixels.
 * @param  {IPositionParams} positionParams  Parameters of grid needed for coordinates calculations.
 * @param  {Number}  x                      X coordinate in grid units.
 * @param  {Number}  y                      Y coordinate in grid units.
 * @param  {Number}  w                      W coordinate in grid units.
 * @param  {Number}  h                      H coordinate in grid units.
 * @return {Position}                       Object containing coords.
 */
const calcGridItemPosition = (
  positionParams: IPositionParams,
  x: number,
  y: number,
  w: number,
  h: number,
  movedData?: { movedType: 'dragging' | 'resizing'; movedPos: Partial<TPosition> }
): TPosition => {
  const { margin, containerPadding, rowHeight } = positionParams
  const colWidth = calcGridColWidth(positionParams)

  const out = { top: 0, left: 0, width: 0, height: 0 }

  if (
    movedData &&
    movedData.movedType === 'resizing' &&
    typeof movedData.movedPos?.width === 'number' &&
    typeof movedData.movedPos?.height === 'number'
  ) {
    out.width = Math.round(movedData.movedPos.width)
    out.height = Math.round(movedData.movedPos.height)
  } else {
    out.width = calcGridItemWHPx(w, colWidth, margin[0])
    out.height = calcGridItemWHPx(h, rowHeight, margin[1])
  }

  if (movedData && typeof movedData.movedPos?.top === 'number' && typeof movedData.movedPos?.left === 'number') {
    out.top = Math.round(movedData.movedPos.top)
    out.left = Math.round(movedData.movedPos.left)
  } else {
    out.top = Math.round((rowHeight + margin[1]) * y + containerPadding[1])
    out.left = Math.round((colWidth + margin[0]) * x + containerPadding[0])
  }
  return out
}

/**
 * Translate x and y coordinates from pixels to grid units.
 * @param  {IPositionParams} positionParams  Parameters of grid needed for coordinates calculations.
 * @param  {Number} top                     Top position (relative to parent) in pixels.
 * @param  {Number} left                    Left position (relative to parent) in pixels.
 * @param  {Number} w                       W coordinate in grid units.
 * @param  {Number} h                       H coordinate in grid units.
 * @return {Object}                         x and y in grid units.
 */
const calcXY = (
  positionParams: IPositionParams,
  top: number,
  left: number,
  w: number,
  h: number
): { x: number; y: number } => {
  const { margin, containerPadding, cols, rowHeight, maxRows } = positionParams
  const colWidth = calcGridColWidth(positionParams)

  // left = containerPaddingX + x * (colWidth + marginX)
  // x * (colWidth + marginX) = left - containerPaddingX
  // x = (left - containerPaddingX) / (colWidth + marginX)
  let x = Math.round((left - containerPadding[0]) / (colWidth + margin[0]))
  let y = Math.round((top - containerPadding[1]) / (rowHeight + margin[1]))

  // Capping
  x = clamp(x, 0, cols - w)
  y = clamp(y, 0, maxRows - h)
  return { x, y }
}

/**
 * Given a height and width in pixel values, calculate grid units.
 * @param  {IPositionParams} positionParams  Parameters of grid needed for coordinates calcluations.
 * @param  {Number} height                  Height in pixels.
 * @param  {Number} width                   Width in pixels.
 * @param  {Number} x                       X coordinate in grid units.
 * @param  {Number} y                       Y coordinate in grid units.
 * @param {String} handle                   Resize Handle.
 * @param  {Number} [maxWidth]              Max width in grid units.
 * @param  {Number} [minWidth]              Min width in grid units.
 * @param  {Number} [maxHeight]             Max height in grid units.
 * @param  {Number} [minHeight]             Min height in grid units.
 * @return {Object}                         w, h as grid units.
 */
const calcWH = (
  positionParams: IPositionParams,
  width: number,
  height: number,
  x: number,
  y: number,
  handle: string,
  bounds: IElementBounds
): { w: number; h: number } => {
  const { margin, maxRows, cols, rowHeight } = positionParams
  const colWidth = calcGridColWidth(positionParams)
  const { maxWidth, minWidth = 1, maxHeight, minHeight = 1 } = bounds
  // width = colWidth * w - (margin * (w - 1))
  // ...
  // w = (width + margin) / (colWidth + margin)
  let w = Math.round((width + margin[0]) / (colWidth + margin[0]))
  let h = Math.round((height + margin[1]) / (rowHeight + margin[1]))

  // Capping
  let _w = clamp(w, minWidth, cols - x)
  let _h = clamp(h, minHeight, maxRows - y)

  if (maxWidth && maxWidth < cols - x) {
    _w = clamp(w, minWidth, maxWidth)
  } else if (['sw', 'w', 'nw'].indexOf(handle) !== -1) {
    _w = clamp(w, minWidth, cols)
  }
  if (maxHeight && maxHeight < maxRows - y) {
    _h = clamp(h, minHeight, maxHeight)
  } else if (['nw', 'n', 'ne'].indexOf(handle) !== -1) {
    _h = clamp(h, minHeight, maxRows)
  }

  return { w: _w, h: _h }
}

// Similar to _.clamp
const clamp = (num: number, lowerBound: number, upperBound: number): number => {
  return Math.max(Math.min(num, upperBound), lowerBound)
}

const getHandle = (dx: number, dy: number) => {
  switch (true) {
    case dy > 0 && dx > 0:
      return 'se'
    case dy < 0 && dx < 0:
      return 'nw'
    case dy > 0 && dx < 0:
      return 'sw'
    case dy < 0 && dx > 0:
      return 'ne'
    case dy < 0:
      return 'n'
    case dx < 0:
      return 'w'
    case dy > 0:
      return 's'
    case dx > 0:
      return 'e'
    default:
      null
  }
}

const calcWHBoundsToPx = (colSize: number, rowSize: number, margin: [number, number], bounds: IElementBounds) => {
  const { maxWidth, minWidth = 1, maxHeight, minHeight = 1 } = bounds
  return {
    maxWidth: calculatePositionUtils.calcGridItemWHPx(maxWidth, colSize, margin[0]),
    minWidth: calculatePositionUtils.calcGridItemWHPx(minWidth, colSize, margin[0]),
    maxHeight: calculatePositionUtils.calcGridItemWHPx(maxHeight, rowSize, margin[1]),
    minHeight: calculatePositionUtils.calcGridItemWHPx(minHeight, rowSize, margin[1]),
  }
}

const calculatePositionUtils = {
  calcGridItemPosition,
  calcXY,
  calcWH,
  calcGridColWidth,
  getHandle,
  clamp,
  calcGridItemWHPx,
  calcWHBoundsToPx,
}

export default calculatePositionUtils
